import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.1.
 *
 * It fixes neither width nor columns: the grid is decided by the screen using
 * it.
 */
const cardVariants = cva(
  // `shadow-raised`: the scale's lowest step, the one for what rests on the
  // page. `Card` is that level's canonical consumer.
  'flex flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-raised',
);

const cardBodyVariants = cva('flex-1', {
  variants: {
    padding: {
      // For full-bleed tables (`DataTable`) inside the card: with no lateral or
      // top padding, the columns and rows reach the edge.
      none: '',
      sm: 'p-inset-sm',
      md: 'p-inset',
    },
  },
  defaultVariants: {
    padding: 'md',
  },
});

export interface CardProps
  extends Omit<React.ComponentPropsWithoutRef<'section'>, 'title'> {
  /** When present, it renders an <h3> and ties the section's aria-labelledby. */
  title?: React.ReactNode;
  /** To the right of the title. */
  actions?: React.ReactNode;
  footer?: React.ReactNode;
  /** `none` for full-bleed tables inside the card. */
  padding?: 'none' | 'sm' | 'md';
}

/**
 * A layout card (7 layers, §4 L4). It is neither a button nor an `<article>` of
 * independent content: it is a `<section>` — a region with a name only when
 * `title` names it.
 *
 * `title` redefines `<section>`'s native attribute of the same name (HTML's
 * tooltip): that is why `CardProps` excludes it from
 * `ComponentPropsWithoutRef<'section'>` with `Omit` before declaring it again
 * with its own type. Without the `Omit`, `React.ReactNode` would not be
 * assignable to the native `string | undefined` and TypeScript would reject it;
 * with a named prop of its own, it does not leak to the DOM as
 * `title="[object Object]"` either.
 *
 * The `aria-labelledby` id comes from `useId()`: an id generated by React,
 * stable across renders and unique even with many cards on the same screen — the
 * alternative of fabricating it by hand (`card-title`) collides as soon as there
 * is a second card, and a screen reader announces an unnamed "section" instead
 * of the real title.
 *
 * With no `title`, neither `aria-labelledby` nor any other accessible name is
 * added: by HTML-AAM's rules an unnamed `<section>` is not exposed as a "region"
 * landmark, so no special case is needed to avoid the "orphan region" — denying
 * it a name is enough.
 */
export function Card({
  title,
  actions,
  footer,
  padding,
  className,
  children,
  ...props
}: CardProps): React.JSX.Element {
  const headingId = React.useId();
  const hasHeader = title != null || actions != null;

  return (
    <section
      className={cn(cardVariants(), className)}
      aria-labelledby={title != null ? headingId : undefined}
      {...props}
    >
      {hasHeader ? (
        <div className="flex items-start justify-between gap-inset-sm border-b border-border p-inset">
          {title != null ? (
            <h3 id={headingId} className="text-h4 text-fg">
              {title}
            </h3>
          ) : (
            <span />
          )}
          {actions != null ? (
            <div className="flex shrink-0 items-center gap-inset-sm">
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className={cn(cardBodyVariants({ padding }))}>{children}</div>
      {footer != null ? (
        <div className="border-t border-border p-inset">{footer}</div>
      ) : null}
    </section>
  );
}
